import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { graphql } from '@api/generated/gql';
import useService from '@modules/ServiceModule/useService';
import ServiceModule from '@modules/ServiceModule';
import { QueueRow } from '../QueuesTable/types';

const getQueuesForTransferQuery = graphql(`
  query getQueuesForTransfer {
    queuesForTransfer {
      id
      name
      entrypoints
      metadata {
        transferRollover {
          destinationType
          workflowId
          workflowExtensions
        }
      }
    }
  }
`);

/**
 * Provides queue data for the transfer modal: queue list and dial targets from
 * `queuesForTransfer`, with `availableUsers` and `estimatedWait` from RTCS
 * service state (same mapping as `useServiceQueuesState`).
 */
export const useTransferQueuesState = () => {
  const { data, loading: gqlLoading } = useQuery(getQueuesForTransferQuery);
  const {
    queueById,
    userByContactCenterUserId,
    loading: serviceLoading,
  } = useService();

  const queues: QueueRow[] = useMemo(
    () =>
      (data?.queuesForTransfer ?? []).map((queue) => {
        const svc = queueById[queue.id];
        const availableUsers = svc
          ? svc.memberIds.filter((memberId) => {
              const user = userByContactCenterUserId[memberId];
              return user ? ServiceModule.isUserAvailable(user) : false;
            }).length
          : 0;

        return {
          id: queue.id,
          name: queue.name,
          number:
            queue.metadata?.transferRollover?.workflowExtensions?.[0] ??
            queue.entrypoints[0] ??
            '',
          availableUsers,
          estimatedWait: svc?.status?.estimatedWait,
        };
      }),
    [data, queueById, userByContactCenterUserId],
  );

  return { queues, loading: gqlLoading || serviceLoading };
};
